import { SharingMinistryType, SmiPlan } from "../../types/supabase"
import { MinistryContext } from "../../lib/rules/chooseSubmissionType"
import { RootState } from "../store"

/**
 * The member's sharing ministry. Org-level ministry takes precedence; member-level
 * is the fallback for orgs that allow mixed memberships. Either may be null for
 * accounts with no sharing ministry.
 *
 * Ministry identity is membership/organization data, not an organization
 * customization: `Customizations` carries per-organization rules layered on top
 * of a base sharing ministry plan (HRA, personal responsibility, advances), so
 * the ministry itself and its tier data live here instead.
 */
export function selectSharingMinistry(s: RootState): SharingMinistryType {
  return (
    (s.membership?.organization?.sharing_ministry_type as SharingMinistryType | null) ??
    (s.membership?.sharing_ministry_type as SharingMinistryType | null) ??
    null
  )
}

/**
 * The Sedera member's chosen Initial Unshareable Amount (IUA). Applies per
 * medical need; expenses on a single incident must reach this amount before
 * the incident becomes eligible to submit to Sedera. Null for non-Sedera
 * members and for Sedera members whose membership hasn't recorded an IUA.
 */
export function selectSederaIuaAmount(s: RootState): number | null {
  if (selectSharingMinistry(s) !== 'Sedera') { return null }
  return s.membership?.iua_amount ?? null
}

/**
 * The Samaritan Ministries member's plan level ('classic' | 'basic'), from
 * `memberships.smi_plan`. Null for non-SMI members and for SMI members whose
 * membership hasn't recorded a plan. Plan-derived limits come from
 * `getSmiPlanLimits` in lib/rules/limits.ts.
 */
export function selectSmiPlan(s: RootState): SmiPlan | null {
  if (selectSharingMinistry(s) !== 'SMI') { return null }
  return (s.membership?.smi_plan as SmiPlan | null) ?? null
}

/**
 * The ministry identity + tier data consumed by `chooseSubmissionType` and the
 * todo generators. Mirrored server-side by the fields on
 * `TodoReconciliation\Data\MembershipData`.
 *
 * This is deliberately a FLAT bag rather than the discriminated `Ministry`
 * union below: it's the rules/PHP boundary, and `chooseSubmissionType` (plus
 * its PHP port) checks each ministry independently, so a flat shape avoids
 * narrowing noise on both sides. UI code should prefer `selectMinistry`.
 */
export function selectMinistryContext(s: RootState): MinistryContext {
  return {
    sharingMinistry: selectSharingMinistry(s),
    sederaIuaAmount: selectSederaIuaAmount(s),
    smiPlan: selectSmiPlan(s),
  }
}

/**
 * The member's sharing ministry and its tier data as a discriminated union, so
 * tier fields travel with the ministry and can't be read for the wrong plan
 * (the type system stops you from reading `iuaAmount` for a CHM member). This
 * is the preferred shape for UI code; `selectSharingMinistry` remains for the
 * many call sites that only need the bare ministry enum.
 */
export type Ministry =
  | { type: 'CHM' }
  | { type: 'Sedera'; iuaAmount: number | null }
  | { type: 'SMI'; plan: SmiPlan | null }
  | { type: null }

export function selectMinistry(s: RootState): Ministry {
  const sharingMinistry = selectSharingMinistry(s)
  switch (sharingMinistry) {
    case 'CHM':
      return { type: 'CHM' }
    case 'Sedera':
      return { type: 'Sedera', iuaAmount: selectSederaIuaAmount(s) }
    case 'SMI':
      return { type: 'SMI', plan: selectSmiPlan(s) }
    default:
      return { type: null }
  }
}
